import { useEffect, useRef } from "react";
import { circadiaEngineProvider } from "../AppController";
import { useCircadiaBridgeReady } from "./useCircadiaBridgeReady";
import { useAppDispatch, useAppSelector } from "./redux";
import {
  setAssignedDevice,
  stopDeviceOnlinePolling,
} from "../redux/Clinic/clinic.slice";

/**
 * Parsed shape of a `device_online` silent push payload from the backend.
 *
 * Fired by the cloud when a device the user just finished provisioning first
 * connects to AWS IoT (publishing `CIRCADIA_AWS_MSG_CONNECTED`). Replaces the
 * client-side AP-fallback watch — push beats poll on latency, works while
 * backgrounded, and doesn't depend on the device firmware's recovery
 * behaviour for an authoritative signal.
 */
interface DeviceOnlinePushPayload {
  type: "device_online";
  device_id: string;
  ts?: number;
}

/**
 * Locate a `device_online` payload anywhere in the platform-shaped push
 * envelope. iOS/Android can deliver payloads at the top level, nested under
 * `data`, or under `notification.data` depending on whether they're routed
 * through Capacitor's PushNotifications, our bridge wrapper, or a silent
 * background callback. Check all the spots and accept the first match.
 *
 * Returns `null` when the payload isn't a device_online event (or is
 * malformed) — caller silently drops in that case.
 */
function parseDeviceOnlinePayload(
  payload: unknown
): DeviceOnlinePushPayload | null {
  if (!payload || typeof payload !== "object") return null;

  const candidates: unknown[] = [payload];
  const top = payload as {
    data?: unknown;
    notification?: { data?: unknown };
  };
  if (top.data && typeof top.data === "object") candidates.push(top.data);
  if (top.notification?.data && typeof top.notification.data === "object") {
    candidates.push(top.notification.data);
  }

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;
    const obj = candidate as Record<string, unknown>;
    if (
      obj.type === "device_online" &&
      typeof obj.device_id === "string" &&
      obj.device_id.trim().length > 0
    ) {
      return {
        type: "device_online",
        device_id: obj.device_id,
        ts: typeof obj.ts === "number" ? obj.ts : undefined,
      };
    }
  }
  return null;
}

/**
 * Strip dashes + lowercase for device-id comparison. Redux stores the id
 * dash-stripped (see `ConnectDevice` provisioning dispatch), and the backend
 * may send either form — normalise both sides.
 */
function normaliseDeviceId(id: string): string {
  return id.replace(/-/g, "").toLowerCase();
}

/**
 * Module-level latch — `setNotifReceivedCb` registers ONE callback at the
 * native bridge layer. Re-mounting the hook (React strict-mode / route
 * changes) must not double-register and clobber the previous handler.
 */
let listenerInstalled = false;

/**
 * Global router for native push-notification payloads.
 *
 * Mount once at the top of the Mobile layout. The hook listens on the
 * `notifReceived` bridge channel (fired for ALL incoming pushes —
 * foreground delivery AND silent background pushes) and routes payloads
 * by `type`.
 *
 * Currently handles:
 *  - `device_online` — marks the in-progress provisioning as online and
 *    stops the cloud poll. Cross-checks the push's `device_id` against
 *    `state.clinic.deviceOnlinePolling.deviceId` so a stale push (or a
 *    push for an unrelated device) is silently dropped.
 *
 * Idempotent — re-mounting won't double-register.
 */
export function useNativePushNotificationRouter(): void {
  const dispatch = useAppDispatch();
  const assignedDevice = useAppSelector((s) => s.clinic.assignedDevice);
  const deviceOnlinePolling = useAppSelector(
    (s) => s.clinic.deviceOnlinePolling
  );
  // On Android the bridge is injected after this hook first mounts; re-run the
  // registration effect once it lands so the push listener is actually wired.
  const bridgeReady = useCircadiaBridgeReady();

  // Refs so the long-lived bridge callback always reads the latest values.
  // The callback is registered once and lives for the lifetime of the
  // session — we can't recreate it whenever React state changes.
  const dispatchRef = useRef(dispatch);
  const assignedDeviceRef = useRef<IAssignedDevice | null>(assignedDevice);
  const pollingRef = useRef(deviceOnlinePolling);
  dispatchRef.current = dispatch;
  assignedDeviceRef.current = assignedDevice;
  pollingRef.current = deviceOnlinePolling;

  useEffect(() => {
    if (!bridgeReady || !circadiaEngineProvider.isWindowCircadia()) return;

    const engine = circadiaEngineProvider as unknown as {
      setNotifReceivedCb?: (cb: ((payload: unknown) => void) | null) => void;
    };

    if (typeof engine.setNotifReceivedCb !== "function") return;
    if (listenerInstalled) return;
    listenerInstalled = true;

    engine.setNotifReceivedCb((payload: unknown) => {
      const parsed = parseDeviceOnlinePayload(payload);
      if (!parsed) return;

      // We only act on pushes for a device the user is actively waiting on.
      // If polling state is null the user has moved on (or never started a
      // provisioning) — silently drop to avoid acting on a stale push from
      // a previous setup attempt.
      const polling = pollingRef.current;
      if (!polling) return;

      if (
        normaliseDeviceId(parsed.device_id) !==
        normaliseDeviceId(polling.deviceId)
      ) {
        return;
      }

      const device = assignedDeviceRef.current;
      if (device) {
        dispatchRef.current(
          setAssignedDevice({ ...device, device_conn_status: 1 })
        );
      }
      dispatchRef.current(stopDeviceOnlinePolling());
    });

    // No teardown — this is an app-lifetime listener. The Mobile layout
    // remounting on route changes should NOT unregister it (we'd miss
    // pushes during the unmount/remount gap). The module-level latch
    // prevents double registration.
  }, [bridgeReady]);
}
